import { styleConstants } from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { createSetIsDowngradeBarVisible } from 'src/app/AlertBar/redux/alertBar.actions'
import OrgRoleAcknowledgementsPopup from 'src/components/OrgRoleAcknowledgementsPopup/OrgRoleAcknowledgementsPopup'
import SystemAlertPopup from 'src/components/SystemAlertPopup/SystemAlertPopup'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { ScreeningStatus } from 'src/models/virtualCard.models'
import Lnk from 'src/navigation/Lnk'
import { PRICING_ROUTE, userScreeningInfoUrl } from 'src/navigation/urls'
import { acknowledgementsQueries } from 'src/queries/organizations/acknowledgements/acknowledgements.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  useQueryOrganizationSaasTier,
  useQueryOrganizationUsages,
} from 'src/queries/organizations/saas.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { createSetIsAcknowledgementAlertBarOpen } from 'src/redux/actions/app.actions'
import {
  selectAlertMessageFromState,
  selectIsAlertBarOpenFromState,
  selectIsDowngradeBarOpenFromState,
  selectMessageCountFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectUserHasFailScreening,
  selectUserHasForbiddenScreening,
} from 'src/redux/selectors/auth.selectors'
import {
  selectAcknowledgementPushdown,
  selectAlertBarPushdown,
  selectDowngradePushdown,
  selectFailPushdown,
  selectForbiddenPushdown,
} from 'src/redux/selectors/css.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

import SvgArrowRightLoD from '../Svgs/SvgArrowRightLoD'
import FixedAlertBanner from './FixedAlertBanner'

const AlertBars: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const isAlertBarOpen = useSelector(selectIsAlertBarOpenFromState)

  const user = useCurrentUser()
  const org = useSelector(selectUserExplicitOrganizationListModelFromState)
  const orgId = org?.id

  const { data: orgFeatureFlags, status: orgFFStatus } =
    useQueryOrganizationFeatureFlags({ orgId, enabled: orgId !== undefined })
  const { data: orgAcknowledgements, status: orgAcknowledgementsStatus } =
    useQuery({
      ...acknowledgementsQueries.org(),
      enabled:
        orgFeatureFlags !== undefined &&
        orgFeatureFlags?.rolesMigrationAcksEnabled,
    })

  // The legacy mount effect dispatched two OTHER keys as prefetches for
  // sibling components (dropped — those readers fetch for themselves now);
  // these are the two stats this component actually reads.
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsActiveSourceCanIssueVirtualCardsCount,
      StatisticsKeys.CreditCardsPendingSourceUserMeCount,
    ] as const,
  })
  const userActiveAccounts =
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceCanIssueVirtualCardsCount
    ].data ?? 0
  const userPendingAccounts =
    statisticsResults[StatisticsKeys.CreditCardsPendingSourceUserMeCount]
      .data ?? 0
  const userActiveAndPendingAccounts = userActiveAccounts + userPendingAccounts

  const [showSystemAlertPopup, setShowSystemAlertPopup] =
    useState<boolean>(false)
  const [showAcknowledgeRoleChangesPopup, setShowAcknowledgeRoleChangesPopup] =
    useState<boolean>(true)

  const dispatch = useBackgroundDispatch()

  const { data: orgUsages } = useQueryOrganizationUsages({
    orgId: orgId,
  })

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: orgId,
    enabled: !!orgId,
  })

  useEffect(() => {
    if (user === undefined || orgUsages === undefined) {
      return
    }
    const showDowngradeMessage =
      user.hasOrgAdminPermissions &&
      currentPlan?.tier === 'STARTER' &&
      (!orgUsages.features.ORGANIZATION_TOTAL_SIZE?.withinLimit ||
        !orgUsages.features.GUEST_ROLE?.withinLimit)
    dispatch(createSetIsDowngradeBarVisible(showDowngradeMessage))
  }, [user, orgUsages, currentPlan])

  const alertMessage = useSelector(selectAlertMessageFromState)
  const messageCount = useSelector(selectMessageCountFromState)

  const userHasForbiddenScreening = useSelector(selectUserHasForbiddenScreening)
  const userHasFailScreening = useSelector(selectUserHasFailScreening)
  const [
    userHasNotAcknowledgedRoleUpdates,
    setUserHasNotAcknowledgedRoleUpdates,
  ] = useState<boolean>(false)

  const alertBarPushdown = useSelector(selectAlertBarPushdown)
  const forbiddenPushdown = useSelector(selectForbiddenPushdown)
  const failPushdown = useSelector(selectFailPushdown)
  const downgradePushdown = useSelector(selectDowngradePushdown)
  const acknowledgementPushDown = useSelector(selectAcknowledgementPushdown)
  const isDowngradeBarVisible = useSelector(selectIsDowngradeBarOpenFromState)

  useEffect(() => {
    const showAlertBanner = orgFeatureFlags?.rolesMigrationAcksEnabled
      ? !orgAcknowledgements?.acknowledgement.acknowledged &&
        userActiveAndPendingAccounts > 0
      : false
    if (orgAcknowledgementsStatus === 'success' && orgFFStatus === 'success') {
      dispatch(createSetIsAcknowledgementAlertBarOpen(showAlertBanner))
      setUserHasNotAcknowledgedRoleUpdates(showAlertBanner)
    }
  }, [
    orgAcknowledgementsStatus,
    orgAcknowledgements,
    orgFFStatus,
    userActiveAndPendingAccounts,
  ])

  useEffect(() => {
    const showAlertBanner = orgFeatureFlags?.rolesMigrationAcksEnabled
      ? !orgAcknowledgements?.acknowledgement.acknowledged &&
        userActiveAndPendingAccounts > 0
      : false
    if (orgAcknowledgementsStatus === 'success') {
      dispatch(createSetIsAcknowledgementAlertBarOpen(showAlertBanner))
      setUserHasNotAcknowledgedRoleUpdates(showAlertBanner)
    }
  }, [orgAcknowledgements])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        width: calc(100%);
        max-width: calc(100%);
      `}
    >
      {isAlertBarOpen && (
        <>
          <FixedAlertBanner
            variant={'soft'}
            title={translate('System alert')}
            message={alertMessage}
            messageCount={messageCount}
            link={
              <span
                onClick={(): void => setShowSystemAlertPopup(true)}
                onKeyPress={(): void => setShowSystemAlertPopup(true)}
                role="button"
                tabIndex={0}
                aria-label={'view-status-button'}
                css={css`
                  cursor: pointer;
                `}
              >
                {translate('View status')}
              </span>
            }
            top={alertBarPushdown}
            zIndex={7}
            data-testid={'system-alert-banner'}
          />
          {showSystemAlertPopup && (
            <SystemAlertPopup
              onClose={(): void => setShowSystemAlertPopup(false)}
            />
          )}
        </>
      )}
      {userHasForbiddenScreening && (
        <FixedAlertBanner
          variant={'severe'}
          title={translate('Alert')}
          message={translate('You have been locked for security concerns.')}
          link={
            <Lnk
              ariaLabelSource={'Learn more'}
              to={{
                pathname: userScreeningInfoUrl(),
                search: `?status=${ScreeningStatus.Forbidden}`,
                state: {},
                hash: '',
              }}
            >
              {translate('Learn more')}
            </Lnk>
          }
          top={forbiddenPushdown}
          zIndex={7}
          data-testid={'forbidden-screening-banner'}
        />
      )}
      {userHasFailScreening && (
        <FixedAlertBanner
          variant={'warning'}
          title={translate('Alert')}
          message={translate('You have been flagged for security concerns.')}
          link={
            <Lnk
              ariaLabelSource={'Learn more'}
              to={{
                pathname: userScreeningInfoUrl(),
                search: `?status=${ScreeningStatus.Fail}`,
                state: {},
                hash: '',
              }}
            >
              {translate('Learn more')}
            </Lnk>
          }
          top={failPushdown}
          zIndex={7}
          data-testid={'fail-screening-banner'}
        />
      )}
      {isDowngradeBarVisible && (
        <FixedAlertBanner
          variant={'warning'}
          title={translate('Alert')}
          message={translate(
            'Your organization is on the Starter plan, which includes up to 5 active users and 10 guests. To continue using Extend, please deactivate excess users or upgrade.',
          )}
          link={
            <a
              href={PRICING_ROUTE()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={'pricing-link'}
              css={css`
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 2px;
              `}
            >
              {translate('Subscribe now')}
              <SvgArrowRightLoD
                height={14}
                width={14}
                fillColor={styleConstants.colors.font_gray_dark}
              />
            </a>
          }
          top={downgradePushdown}
          zIndex={7}
          data-testid={'downgrade-banner'}
        />
      )}
      {userHasNotAcknowledgedRoleUpdates && (
        <>
          <FixedAlertBanner
            variant={'warning'}
            title={translate('ACTION REQUIRED')}
            message={translate(
              'Please review and acknowledge role permission changes to continue using the most up-to-date version of Extend.',
            )}
            link={
              <span
                onClick={(): void => setShowAcknowledgeRoleChangesPopup(true)}
                onKeyPress={(): void =>
                  setShowAcknowledgeRoleChangesPopup(true)
                }
                role="button"
                tabIndex={0}
                aria-label={'click to acknowledge changes'}
                css={css`
                  cursor: pointer;
                `}
              >
                {translate('Click here to acknowledge changes')}
              </span>
            }
            top={acknowledgementPushDown}
            zIndex={7}
            data-testid={'role-acknowledgement-banner'}
          />
          {showAcknowledgeRoleChangesPopup && (
            <OrgRoleAcknowledgementsPopup
              onClose={(): void => setShowAcknowledgeRoleChangesPopup(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

export default AlertBars
